import { Stack, Text } from '@repletoryreach/sdk-ui';
import { DataTable } from '@repletoryreach/sdk-ui/data-display';
import { BarList } from './BarList';
import { useT } from '../../../i18n/I18nProvider';
import { formatCount } from '../../../lib/format';
import type { Indexed } from '../../../lib/types';
import { stopStats, type StopStat } from '../lib/data';
import { MUTED, Muted, Panel, SeeChats } from '../shared';
import type { TabProps } from './tab';

/**
 * Where customers stopped without what they came for: why (the AI's groups), after what, and the flow steps runs
 * stopped at because nobody answered the step's question (counted for the whole period).
 */
export function Stopped({ report, rows, onSee }: TabProps) {
  const t = useT();
  const stats = stopStats(rows);
  const describe = new Map((report.taxonomy?.stop_groups ?? []).map((x) => [x.name, x.description]));
  const points = (report.metrics?.flows ?? []).flatMap((f) => f.stop_points.map((p) => ({ ...p, flow: f.name, key: `${f.flow_id}:${p.node_id}` })))
    .sort((a, b) => b.count - a.count);

  return (
    <Stack gap="lg">
      <Panel title={t('insights.stopped.title')} note={t('insights.stopped.note')} testId="insights-stopped">
        {stats.length === 0 ? <Muted>{t('insights.stopped.none')}</Muted> : (
          <>
            <BarList label={t('insights.stopped.title')} items={stats.slice(0, 12).map((s) => ({ name: s.name, value: s.count }))} />
            <div className="rr-insights__table-scroll">
              <DataTable<Indexed<StopStat>>
                records={stats as Indexed<StopStat>[]}
                idAccessor="name"
                columns={[
                  { accessor: 'name', title: t('insights.stopped.reason'), render: (s) => (
                    <Stack gap={0}>
                      <Text role="bodySmall" fw={600}>{s.name}</Text>
                      {describe.get(s.name) && <Text role="caption" c={MUTED}>{describe.get(s.name)}</Text>}
                    </Stack>
                  ) },
                  { accessor: 'count', title: t('insights.topics.chats'), textAlign: 'right', render: (s) => formatCount(s.count) },
                  { accessor: 'examples', title: t('insights.stopped.after'), render: (s) => (
                    <Stack gap={0}>{s.examples.map((e) => <Text key={e.ref} role="caption" c={MUTED}>{e.ref}: {e.after}</Text>)}</Stack>
                  ) },
                  { accessor: 'refs', title: '', render: (s) => <SeeChats refs={s.refs} onSee={onSee} t={t} /> }
                ]}
              />
            </div>
          </>
        )}
      </Panel>

      <Panel title={t('insights.stopped.steps')} note={t('insights.stopped.stepsNote')}>
        {points.length === 0 ? <Muted>{t('insights.stopped.noSteps')}</Muted> : (
          <div className="rr-insights__table-scroll">
            <DataTable<Indexed<(typeof points)[number]>>
              records={points as Indexed<(typeof points)[number]>[]}
              idAccessor="key"
              columns={[
                { accessor: 'flow', title: t('insights.bot.flow'), render: (p) => <Text role="bodySmall">{p.flow}</Text> },
                { accessor: 'label', title: t('insights.stopped.step'), render: (p) => <Text role="bodySmall" fw={600}>{p.label}</Text> },
                { accessor: 'count', title: t('insights.stopped.runs'), textAlign: 'right', render: (p) => formatCount(p.count) }
              ]}
            />
          </div>
        )}
      </Panel>
    </Stack>
  );
}
